const NAVY = '#0D2461', AMBER = '#F5A800', BORDER = '#c8d0e0';

const VERSIONS = [
  {
    version: 'v1.7.0',
    date: 'April 2026',
    title: 'Roles, Approvals & Auditor Dashboard',
    items: [
      'New Manager role — managers approve sample plans before a draw can be made',
      'Submit for Approval workflow — members submit plans, managers approve or reject',
      'New Auditor role with platform-wide read access, project flagging, and team suspension',
      'Super Admin role for platform owner with full access including auditor dashboard',
      'Client/Taxpayer Group field on projects — links multiple projects for the same client',
      'Auditor dashboard automatically detects clients with multiple draws (potential workarounds)',
    ],
  },
  {
    version: 'v1.6.0',
    date: 'April 2026',
    title: 'Advanced Sampling Engine',
    items: [
      'Visual strata slider — dot-plot population distribution with draggable boundary handles on log scale',
      'Projected relative precision display (assumes 100% qualification) — updates live as strata change',
      'Toss stratum — exclude bottom X% of population by dollar value from sampling (toggle, default 2%)',
      'Dual estimator — Mean and Difference estimators computed in parallel; user selects which to report',
      'Small sample warnings when total sample < 30 or any stratum n < 5',
      'Sample run log — every draw logged with seed, settings, and projected RP; visible in Results',
    ],
  },
  {
    version: 'v1.5.0',
    date: 'April 2026',
    title: 'Archive, Strata & Engine Improvements',
    items: [
      'Project archiving — admin users can archive completed projects to keep dashboard clean',
      'Archived projects collapsible at bottom of dashboard, can be unarchived at any time',
      'Variable strata count (2–8) with button group selector — default 5',
      'Distinct color coding for each stratum in the stratification table',
      'Fixed data persistence — all project data now reliably reloads when returning to a project',
    ],
  },
  {
    version: 'v1.4.0',
    date: 'April 2026',
    title: 'Phase 2 — Full Sampling Workflow',
    items: [
      'Population upload with drag-and-drop, automatic column detection, and preview',
      'Full stratification controls — fixed, per-stratum override, and precision formula modes',
      'Auto-saving scoring interface — scores save on every field exit, no Save button needed',
      'Statistical results — point estimate, standard error, confidence intervals, three-tier rule',
      'Per-tax-year QRE breakdown using dollar apportionment',
      'Pause screen with downloadable project list for client review',
      'CSV workpaper export for Appendix B documentation',
    ],
  },
  {
    version: 'v1.3.0',
    date: 'March 2026',
    title: 'Team Management',
    items: [
      'Admin users can add and remove team members directly from the app',
      'Role assignment (Member, Manager, Admin) with inline dropdown',
      'Team page shows all members with email, role, and join date',
      'New members can log in immediately with temporary credentials',
    ],
  },
  {
    version: 'v1.2.0',
    date: 'March 2026',
    title: 'Credits & Payments',
    items: [
      'Credit balance displayed in navigation bar',
      'Four credit bundle tiers — Single, Standard, Firm, Enterprise',
      'Secure Stripe payment links with automatic credit delivery on purchase',
      'Credits shared across all team members',
    ],
  },
  {
    version: 'v1.1.0',
    date: 'March 2026',
    title: 'Authenticated Platform Launch',
    items: [
      'Secure login with email and password',
      'Multi-tenant architecture — each firm\'s data is fully isolated',
      'Project dashboard with status tracking',
      'Five-step workflow framework (Population → Sample Plan → Client Review → Scoring → Results)',
    ],
  },
  {
    version: 'v1.0.0',
    date: 'Early 2026',
    title: 'Public Calculator',
    items: [
      'Rev. Proc. 2011-42 compliant statistical sampling calculator',
      'Population upload, stratification, random sample draw',
      'Statistical results with three-tier precision rule',
      'Available at thelabstatsampler.netlify.app',
    ],
  },
];

export const CURRENT_VERSION = 'v1.7.0';

export default function WhatsNewModal({ onClose }) {
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <div>
            <div style={eyebrow}>The LAB Sampling Platform</div>
            <h2 style={title}>What's New</h2>
            <p style={sub}>Version history and release notes</p>
          </div>
          <button style={closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Version list */}
        <div style={scrollBox}>
          {VERSIONS.map((v, i) => (
            <div key={v.version} style={{ ...versionBlock, ...(i === 0 ? currentBlock : {}) }}>
              <div style={versionHeader}>
                <div style={versionLeft}>
                  <span style={{ ...versionBadge, ...(i === 0 ? currentBadge : {}) }}>{v.version}</span>
                  {i === 0 && <span style={latestBadge}>Current</span>}
                  <span style={versionTitle}>{v.title}</span>
                </div>
                <span style={versionDate}>{v.date}</span>
              </div>
              <ul style={itemList}>
                {v.items.map((item, j) => (
                  <li key={j} style={itemStyle}>
                    <span style={bullet}>→</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={footer}>
          <div style={footerNote}>
            Phase 3 coming soon: Automated PDF report with Appendix A sampling plan,
            Appendix B workpaper, statistical results, and certification block.
          </div>
          <button style={closeFullBtn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

const overlay      = { position:'fixed', inset:0, background:'rgba(13,36,97,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500, padding:24, backdropFilter:'blur(4px)' };
const modal        = { background:'white', borderRadius:12, width:'100%', maxWidth:680, maxHeight:'88vh', display:'flex', flexDirection:'column', boxShadow:'0 32px 80px rgba(0,0,0,0.3)' };
const header       = { padding:'28px 32px 20px', borderBottom:`1.5px solid ${BORDER}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexShrink:0 };
const eyebrow      = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:AMBER, marginBottom:4 };
const title        = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:22, color:NAVY, margin:'0 0 4px' };
const sub          = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', margin:0 };
const closeBtn     = { background:'none', border:'none', fontSize:18, color:'#8a96b0', cursor:'pointer', padding:'4px 8px', flexShrink:0 };
const scrollBox    = { flex:1, overflowY:'auto', padding:'20px 32px' };
const versionBlock = { padding:'16px 20px', borderRadius:8, border:`1px solid ${BORDER}`, marginBottom:14 };
const currentBlock = { border:`2px solid ${NAVY}`, background:'#f8f9fc' };
const versionHeader= { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 };
const versionLeft  = { display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' };
const versionBadge = { fontFamily:"'Roboto Mono',monospace", fontSize:12, fontWeight:700, color:'#8a96b0', background:'#f0f2f8', padding:'3px 10px', borderRadius:4 };
const currentBadge = { color:NAVY, background:'#e8ecf5' };
const latestBadge  = { fontFamily:"'Roboto Mono',monospace", fontSize:10, fontWeight:700, color:'#1a5c3a', background:'#e4f2eb', padding:'2px 8px', borderRadius:10 };
const versionTitle = { fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:13, color:NAVY };
const versionDate  = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', flexShrink:0 };
const itemList     = { margin:0, padding:0, listStyle:'none' };
const itemStyle    = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#3a4a6a', lineHeight:1.7, display:'flex', gap:8, marginBottom:4 };
const bullet       = { color:AMBER, fontWeight:700, flexShrink:0 };
const footer       = { padding:'16px 32px 24px', borderTop:`1.5px solid ${BORDER}`, flexShrink:0 };
const footerNote   = { fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', marginBottom:14, lineHeight:1.6 };
const closeFullBtn = { padding:'10px 24px', background:NAVY, color:AMBER, border:'none', borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:13, cursor:'pointer' };
